import React from "react";

import { useScopedMemo } from "~/tools/impulse";
import { isPresent } from "~/tools/is-present";
import { Template } from "~/tools/template";
import { useDelayed } from "~/tools/use-delayed";

import { DashboardWidget, type DashboardWidgetProps } from "../_dashboard-widget";

import { DEBOUNCE_QUERIES_MS } from "./_common";
import { FormState } from "./_form-state";

export interface SetupDashboardWidgetPreviewProps extends Pick<DashboardWidgetProps, "timeRange"> {
	form: FormState;
	className?: string;
}

export const SetupDashboardWidgetPreview: React.FC<SetupDashboardWidgetPreviewProps> = ({
	form,
	timeRange,
	...props
}) => {
	const queries = useScopedMemo(
		(scope) => {
			// extract the output per element so it can filter out invalid queries
			return form.fields.kind.branches.metrics.fields.queries
				.getElements(scope)
				.map((element) => element.getOutput(scope))
				.filter(isPresent);
		},
		[form],
	);

	// debounce the queries before passing them to the widget where it fetches the data
	const debouncedQueries = useDelayed(queries, DEBOUNCE_QUERIES_MS);

	const widget = useScopedMemo(
		(scope) => {
			/*
			 * TODO the react-impulse-form has a bug when returning verbose output
			 * https://github.com/owanturist/signal/issues/1071
			 *
			 * The workaround is to call verbose getOutput until the last ImpulseFormSwitch.
			 */

			const output = form.getOutput(scope, (_, verbose) => verbose);

			return FormState.verboseOutputToSpec({
				...output,
				name: form.fields.name.getOutput(scope) ?? Template("\u00A0"), // use the spaceholder for name if not set

				// extract the output per element so it can filter out invalid links
				links: form.fields.links
					.getElements(scope)
					.map((element) => element.getOutput(scope))
					.filter(isPresent),

				kind: {
					...output.kind,
					branches: {
						...output.kind.branches,
						metrics: {
							queries: debouncedQueries,
							settings: {
								active: form.fields.kind.branches.metrics.fields.settings.active.getOutput(scope),
								branches: {
									TimeSeriesChart: form.fields.kind.branches.metrics.fields.settings.branches.TimeSeriesChart.getOutput(
										scope,
										(_, verbose) => verbose,
									),
									BarChart: form.fields.kind.branches.metrics.fields.settings.branches.BarChart.getOutput(
										scope,
										(_, verbose) => verbose,
									),
									GaugeChart: form.fields.kind.branches.metrics.fields.settings.branches.GaugeChart.getOutput(
										scope,
										(_, verbose) => verbose,
									),
									StatChart: form.fields.kind.branches.metrics.fields.settings.branches.StatChart.getOutput(
										scope,
										(_, verbose) => verbose,
									),
								},
							},
						},
					},
				},
			});
		},
		[form, debouncedQueries],
	);

	return <DashboardWidget identity="preview" spec={widget} timeRange={timeRange} {...props} />;
};
